export type SahScheduleCadence = 'weekly' | 'fortnightly' | 'monthly' | 'daily' | 'ad_hoc';

export type SahScheduleIntentInput = {
  cadence: SahScheduleCadence;
  occurrencesPerCadence: number;
  preferredDays?: readonly string[] | null;
  preferredTimeWindow?: string | null;
  notes?: string | null;
  source?: string | null;
  quarterWeeks?: number;
};

export type SahScheduleIntentDerivation = {
  cadence: SahScheduleCadence;
  occurrencesPerCadence: number;
  preferredDays: string[];
  frequencyPerPeriod: number;
  displayLabel: string;
  budgetLabel: string;
  warning: string | null;
};

const DEFAULT_QUARTER_WEEKS = 13;
const DAYS_PER_WEEK = 7;
const MONTHS_PER_QUARTER = 3;
const VALID_PREFERRED_DAYS = new Set([
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
]);

function roundQuarterFrequency(value: number): number {
  return Math.round(value * 100) / 100;
}

function normaliseOccurrenceCount(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function normalisePreferredDays(days: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(days)) return [];
  return Array.from(
    new Set(
      days
        .map((day) =>
          String(day || '')
            .trim()
            .toUpperCase(),
        )
        .filter((day) => Boolean(day) && VALID_PREFERRED_DAYS.has(day)),
    ),
  );
}

function cadenceLabel(cadence: SahScheduleCadence): string {
  switch (cadence) {
    case 'weekly':
      return 'week';
    case 'fortnightly':
      return 'fortnight';
    case 'monthly':
      return 'month';
    case 'daily':
      return 'day';
    case 'ad_hoc':
      return 'ad hoc this quarter';
  }
}

function formatCount(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

export function deriveQuarterFrequencyFromScheduleIntent(
  input: SahScheduleIntentInput,
): SahScheduleIntentDerivation {
  const quarterWeeks =
    Number.isFinite(input.quarterWeeks) && Number(input.quarterWeeks) > 0
      ? Number(input.quarterWeeks)
      : DEFAULT_QUARTER_WEEKS;
  const occurrencesPerCadence = normaliseOccurrenceCount(input.occurrencesPerCadence);
  const preferredDays = normalisePreferredDays(input.preferredDays);
  const fortnightCount = Math.ceil(quarterWeeks / 2);
  const quarterDays = quarterWeeks * DAYS_PER_WEEK;

  const frequencyPerPeriod = (() => {
    switch (input.cadence) {
      case 'weekly':
        return occurrencesPerCadence * quarterWeeks;
      case 'fortnightly':
        return occurrencesPerCadence * fortnightCount;
      case 'monthly':
        return occurrencesPerCadence * MONTHS_PER_QUARTER;
      case 'daily':
        return occurrencesPerCadence * quarterDays;
      case 'ad_hoc':
        return occurrencesPerCadence;
    }
  })();

  const roundedFrequency = roundQuarterFrequency(frequencyPerPeriod);
  const displayLabel =
    input.cadence === 'ad_hoc'
      ? `${formatCount(occurrencesPerCadence)} ad hoc visit${
          occurrencesPerCadence === 1 ? '' : 's'
        } this quarter`
      : `${formatCount(occurrencesPerCadence)} visit${
          occurrencesPerCadence === 1 ? '' : 's'
        } per ${cadenceLabel(input.cadence)}`;

  const warning =
    ['weekly', 'fortnightly', 'monthly'].includes(input.cadence) &&
    preferredDays.length > 0 &&
    occurrencesPerCadence !== preferredDays.length
      ? `Preferred days document roster intent; budget count follows the visits-per-${cadenceLabel(
          input.cadence,
        )} value.`
      : null;

  return {
    cadence: input.cadence,
    occurrencesPerCadence,
    preferredDays,
    frequencyPerPeriod: roundedFrequency,
    displayLabel,
    budgetLabel: `${formatCount(roundedFrequency)} per SAH quarter`,
    warning,
  };
}

export function buildSahScheduleIntentPayload(
  input: SahScheduleIntentInput,
): Record<string, unknown> {
  const derivation = deriveQuarterFrequencyFromScheduleIntent(input);
  return {
    cadence: derivation.cadence,
    occurrencesPerCadence: derivation.occurrencesPerCadence,
    preferredDays: derivation.preferredDays,
    preferredTimeWindow: input.preferredTimeWindow?.trim() || null,
    notes: input.notes?.trim() || null,
    derivedFrequencyPerPeriod: derivation.frequencyPerPeriod,
    frequencyPerPeriod: derivation.frequencyPerPeriod,
    derivationBasis: 'sah_quarter_13_weeks',
    source: input.source || 'care_plan_compose',
  };
}
